import {useCallback} from 'react'
import useDeferredNavigation from './use-deferred-navigation'

const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+.-]*:/i

const normalizeNavigationTarget = (href) => {
    if (!href) {
        return href
    }

    if (typeof window === 'undefined') {
        return ABSOLUTE_URL_PATTERN.test(href) ? href : href
    }

    try {
        const url = new URL(href, window.location.origin)

        if (url.origin !== window.location.origin) {
            return href
        }

        return `${url.pathname}${url.search}${url.hash}`
    } catch {
        return href
    }
}

export const getNavigationProps = (href, isExternalRedirection = false) => {
    const normalizedHref = normalizeNavigationTarget(href)
    const isProtocolHandled = /^(mailto:|tel:|sms:|javascript:)/i.test(normalizedHref || '')
    const isExternalUrl =
        typeof window !== 'undefined' && Boolean(normalizedHref)
            ? (() => {
                  try {
                      return (
                          new URL(normalizedHref, window.location.origin).origin !==
                          window.location.origin
                      )
                  } catch {
                      return ABSOLUTE_URL_PATTERN.test(normalizedHref)
                  }
              })()
            : ABSOLUTE_URL_PATTERN.test(normalizedHref || '')
    const shouldDeferNavigation =
        Boolean(normalizedHref) && !isExternalRedirection && !isProtocolHandled && !isExternalUrl

    return {
        to: normalizedHref,
        isExternal: !shouldDeferNavigation,
        shouldDeferNavigation
    }
}

export default function useNavigationHandler({spinnerRef} = {}) {
    const deferredNavigate = useDeferredNavigation({spinnerRef})

    return useCallback(
        (event, navigationProps, beforeNavigate) => {
            if (!navigationProps?.shouldDeferNavigation) {
                beforeNavigate?.()
                return false
            }

            return deferredNavigate(event, navigationProps.to, beforeNavigate)
        },
        [deferredNavigate]
    )
}
